import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom, map } from 'rxjs';
import { ListingApiService } from '../backend-api/listing-api.service';
import { SiteApiService } from '../backend-api/site-api.service';
import type { IAccessRoute, INoise, IPrivacy, ISite, ISubPhoto, IVehicle } from '@curbnturf/entities';

/** Everything the sites slide-over form collects for one site, flattened
 *  out of the DTOs `SiteService`'s individual PUT endpoints expect. */
export interface ISiteFormData {
  name: string;
  description?: string;
  available?: boolean;
  price: number;
  maxGuests: number;
  maxWidth?: number;
  maxLength?: number;
  maxHeight?: number;
  tentOnly?: boolean;
  amenityTypes: string[];
  vehicles: IVehicle;
  privacy: IPrivacy;
  noise: INoise;
  accessRoutes: IAccessRoute[];
  potentialHazards?: boolean;
  potentialHazardDescription?: string;
  blackoutDays?: string[];
  blackoutMonths?: number[];
  blackoutDayOfWeek?: string[];
}

/**
 * Thin composition layer over the already-existing `SiteService`/`ListingService`
 * HTTP endpoints — deliberately separate from `HostListingDraftService`, which is
 * built around a single `realSiteId` per localStorage draft snapshot and doesn't
 * fit "manage N real sites on an already-published listing." Sites here are real
 * DB rows on a real listing, loaded and saved directly.
 *
 * `save()` intentionally issues one small fixed set of PUT calls per Save click
 * rather than the field-by-field autosave pipelines the legacy edit-sites UI used
 * (name, isolation, price, vehicles, blackout each debounced independently).
 */
@Injectable({ providedIn: 'root' })
export class SitesManagementService {
  private listingSvc = inject(ListingApiService);
  private siteSvc = inject(SiteApiService);

  listSites(listingId: number): Observable<ISite[]> {
    return this.listingSvc.fetch(listingId).pipe(map(listing => listing.sites ?? []));
  }

  async addSite(listingId: number): Promise<ISite> {
    const listing = await firstValueFrom(this.listingSvc.addNewSites(listingId, 1));
    const site = listing.sites?.[listing.sites.length - 1];
    if (!site?.id) throw new Error('Server did not return a new site.');
    return site;
  }

  async save(siteId: number, data: ISiteFormData): Promise<ISite> {
    await firstValueFrom(this.siteSvc.updateName(siteId, {
      name: data.name,
      description: data.description,
      available: data.available,
    }));
    await firstValueFrom(this.siteSvc.updateDetails(siteId, {
      amenities: data.amenityTypes.map(type => ({ type: type as never })),
      maxWidth: data.maxWidth,
      maxLength: data.maxLength,
      maxHeight: data.maxHeight,
      maxGuests: data.maxGuests,
      tentOnly: data.tentOnly,
    }));
    await firstValueFrom(this.siteSvc.updateVehicles(siteId, data.vehicles));
    await firstValueFrom(this.siteSvc.updateIsolation(siteId, {
      noise: data.noise,
      privacy: data.privacy,
      accessRoutes: data.accessRoutes,
      potentialHazards: !!data.potentialHazards,
      potentialHazardDescription: data.potentialHazardDescription,
    }));
    await firstValueFrom(this.siteSvc.updatePrice(siteId, { price: data.price }));
    await firstValueFrom(this.siteSvc.updateBlackoutTimes(siteId, {
      blackoutDays: data.blackoutDays,
      blackoutMonths: data.blackoutMonths,
      blackoutDayOfWeek: data.blackoutDayOfWeek,
    }));

    return firstValueFrom(this.siteSvc.fetch(siteId));
  }

  duplicate(siteId: number, sourceName: string, name?: string): Observable<ISite> {
    return this.siteSvc.duplicateSite(siteId, name?.trim() || `Copy of ${sourceName}`);
  }

  remove(siteId: number): Observable<ISite> {
    return this.siteSvc.remove(siteId);
  }

  enable(site: ISite): Observable<{ success: boolean; message: string; listingDisabled?: boolean }> {
    return this.siteSvc.enable(site);
  }

  disable(site: ISite): Observable<{ success: boolean; message: string; listingDisabled?: boolean }> {
    return this.siteSvc.disable(site);
  }

  uploadPhoto(siteId: number, formData: FormData): Observable<{ id: number; photos: unknown[] }> {
    return this.siteSvc.uploadImage(siteId, formData);
  }

  removePhoto(siteId: number, photoIndex: number): Observable<unknown> {
    return this.siteSvc.removeImage(siteId, photoIndex);
  }

  addPhotoFromListing(siteId: number, key: string): Observable<ISite> {
    return this.siteSvc.addPhotoFromListing(siteId, key);
  }

  updatePhotoDetails(siteId: number, photos: ISubPhoto[]): Observable<ISite> {
    return this.siteSvc.updateSitePhotos(siteId, photos);
  }
}
